import React, { useState, useEffect } from 'react';
import { useSearchParams } from 'react-router-dom';
import { 
  Search, Filter, Grid3X3, List, BarChart3, Plus, 
  Eye, Edit, MoreVertical, Clock, AlertCircle, 
  CheckCircle, Calendar, Phone, User, DollarSign,
  FileText, Briefcase, BookOpen, Calculator, PenTool,
  ClipboardCheck, Archive, RefreshCw, Hourglass,
  CheckSquare, GraduationCap, Award, Target, Users,
  Star, Clock4, BookmarkPlus, Download, Upload
} from 'lucide-react';
import { formatearFechaCompleta, formatearPrecio} from '../../../utils/utils';
import { useSolicitudesDisponiblesModel } from '../../../modelos/Solicitudes/modeloSolicitudes';
import NuevaTesis from './NuevaTesis';
import NuevaCotizacion from './Generar_Cotizacion';

const DashboardTesis = ({ 
  onVerDetalles, 
  onEditarTesis,
  onDescargarTesis,
  onSubirAvance,
  onGenerarPDF,
  onSubmitCotizacion, // Nuevo prop para manejar cotizaciones
  isAdmin = false,
  isDirector = false,
  onCerrarModalNuevaTesis,
  onSubmitNuevaTesis,
}) => {
  // Hook del modelo
  const {
    solicitudesDisponibles,
    loadingSolicitudes,
    errorSolicitudes,
    cargarSolicitudes,
    setupWebSocketsSolicitudes,
  } = useSolicitudesDisponiblesModel();

  const [searchParams, setSearchParams] = useSearchParams();
  const [vista, setVista] = useState(searchParams.get('view') || 'cards');
  const [filtros, setFiltros] = useState({
    busqueda: '',
    fecha: 'todas',
    estado: 'todos',
    modalidad: 'todas', // Presencial, Virtual, Mixta
    disponibilidad: 'todos',
    nivel: 'todos' // Pregrado, Postgrado, Maestría, Doctorado
  });
  const [menuAbierto, setMenuAbierto] = useState(null);

  const [modalNuevaTesisAbierto, setModalNuevaTesisAbierto] = useState(false);
  const [creandoTesis, setCreandoTesis] = useState(false);

  // Estados para el modal de cotización
  const [modalCotizacionAbierto, setModalCotizacionAbierto] = useState(false);
  const [tesisSeleccionada, setTesisSeleccionada] = useState(null);
  const [creandoCotizacion, setCreandoCotizacion] = useState(false);

  // Efecto para cargar datos y configurar WebSockets
  useEffect(() => {
    cargarSolicitudes(true);
    const cleanupWebSockets = setupWebSocketsSolicitudes();
    return () => {
      cleanupWebSockets();
    };
  }, []);

  // Filtrar solo solicitudes de TESIS
  const tesis = solicitudesDisponibles.filter(solicitud => 
    solicitud.Servicio === "TESIS"
  );

  // Función para refrescar
  const onRefrescar = () => {
    cargarSolicitudes(true);
  };

  // Estados específicos para tesis (simplificado)
  const obtenerEstadoTesis = (tesis) => {
    const estadoLower = tesis.Estado?.toLowerCase();
    
    // Verificar cotizaciones para tesis
    const tieneCotizacionesConPrecio = tesis.cotizaciones && 
      tesis.cotizaciones.some(cotizacion => {
        const precio = cotizacion.cotizacion || 0;
        return precio > 0;
      });
    
    // Cotizada
    if (tieneCotizacionesConPrecio) {
      return 'cotizada';
    }
    
    // Disponible para cotizar
    if (estadoLower === "disponible") {
      return 'disponible';
    }
    
    return 'disponible';
  };

  // Categorización por estados de tesis (simplificado)
  const tesisDisponibles = tesis.filter(t => obtenerEstadoTesis(t) === 'disponible');
  const tesisCotizadas = tesis.filter(t => obtenerEstadoTesis(t) === 'cotizada');

  // Información específica para tesis
  const obtenerInfoTesis = (tesis) => {
    const nivel = tesis.nivel || 'PREGRADO';
    
    const mapeoNiveles = {
      'PREGRADO': { 
        icon: <GraduationCap size={20} />, 
        color: 'from-blue-500 to-blue-600', 
        label: 'Pregrado' 
      },
      'POSTGRADO': { 
        icon: <Award size={20} />, 
        color: 'from-green-500 to-green-600', 
        label: 'Postgrado' 
      },
      'MAESTRIA': { 
        icon: <Target size={20} />, 
        color: 'from-yellow-500 to-orange-500', 
        label: 'Maestría' 
      },
      'DOCTORADO': { 
        icon: <Star size={20} />, 
        color: 'from-red-500 to-red-600', 
        label: 'Doctorado' 
      },
    };

    return mapeoNiveles[nivel.toUpperCase()] || { 
      icon: <BookOpen size={20} />, 
      color: 'from-gray-500 to-gray-600', 
      label: 'Tesis' 
    };
  };

  // Calcular urgencia específica para tesis (fechas más largas)
  const calcularUrgenciaTesis = (fechaEntrega) => {
    const ahora = new Date();
    const entrega = new Date(fechaEntrega);
    const diasRestantes = Math.ceil((entrega - ahora) / (1000 * 60 * 60 * 24));

    if (diasRestantes < 0) return { 
      tipo: 'vencida', 
      label: 'Vencida', 
      color: 'from-gray-500 to-gray-600' 
    };
    if (diasRestantes <= 7) return { 
      tipo: 'urgente', 
      label: '< 1 semana', 
      color: 'from-red-500 to-red-600' 
    };
    if (diasRestantes <= 30) return { 
      tipo: 'proximo', 
      label: '< 1 mes', 
      color: 'from-yellow-500 to-orange-500' 
    };
    if (diasRestantes <= 90) return { 
      tipo: 'normal', 
      label: '< 3 meses', 
      color: 'from-green-500 to-green-600' 
    };
    return { 
      tipo: 'planificado', 
      label: `${diasRestantes} días`, 
      color: 'from-blue-500 to-blue-600' 
    };
  };

  // Estadísticas específicas para tesis (simplificado)
  const calcularStatsTesis = () => {
    let tesisParaStats = [];
    
    if (filtros.disponibilidad === 'disponibles') {
      tesisParaStats = tesisDisponibles;
    } else if (filtros.disponibilidad === 'cotizadas') {
      tesisParaStats = tesisCotizadas;
    } else {
      tesisParaStats = tesis;
    }

    const urgentes = tesisParaStats.filter(t => calcularUrgenciaTesis(t.fechaentrega).tipo === 'urgente').length;
    const proximas = tesisParaStats.filter(t => calcularUrgenciaTesis(t.fechaentrega).tipo === 'proximo').length;
    const normales = tesisParaStats.filter(t => calcularUrgenciaTesis(t.fechaentrega).tipo === 'normal').length;
    const planificadas = tesisParaStats.filter(t => calcularUrgenciaTesis(t.fechaentrega).tipo === 'planificado').length;
    const total = tesisParaStats.length;

    return {
      urgentes,
      proximas,
      normales,
      planificadas,
      total,
      label1: getStatsLabel(filtros.disponibilidad),
      totalLabel: getStatsTotal(filtros.disponibilidad)
    };
  };

  const getStatsLabel = (disponibilidad) => {
    switch(disponibilidad) {
      case 'disponibles': return 'Para Cotizar';
      case 'cotizadas': return 'Con Presupuesto';
      default: return 'Total Tesis';
    }
  };

  const getStatsTotal = (disponibilidad) => {
    switch(disponibilidad) {
      case 'disponibles': return 'Disponibles';
      case 'cotizadas': return 'Cotizadas';
      default: return 'Todas';
    }
  };

  const stats = calcularStatsTesis();

  // Cambiar vista y actualizar URL
  const cambiarVista = (nuevaVista) => {
    setVista(nuevaVista);
    setSearchParams({ view: nuevaVista });
  };

  // Filtrar tesis (simplificado)
  const tesisFiltradas = (() => {
    let baseTesis = tesis;
    
    // Filtro por disponibilidad
    if (filtros.disponibilidad === 'disponibles') {
      baseTesis = tesisDisponibles;
    } else if (filtros.disponibilidad === 'cotizadas') {
      baseTesis = tesisCotizadas;
    }
    
    return baseTesis.filter(tesisItem => {
      const cumpleBusqueda = 
        tesisItem.id.toString().toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        tesisItem.titulo?.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        tesisItem.materia.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        tesisItem.resumen.toLowerCase().includes(filtros.busqueda.toLowerCase()) ||
        tesisItem.estudiante?.toLowerCase().includes(filtros.busqueda.toLowerCase());
      
      const cumpleFecha = filtros.fecha === 'todas' || 
                         (filtros.fecha === 'hoy' && new Date(tesisItem.fechaentrega).toDateString() === new Date().toDateString());
      
      const cumpleEstado = filtros.estado === 'todos' || tesisItem.Estado?.toLowerCase() === filtros.estado;
      
      const cumpleNivel = filtros.nivel === 'todos' || tesisItem.nivel?.toLowerCase() === filtros.nivel;

      return cumpleBusqueda && cumpleFecha && cumpleEstado && cumpleNivel;
    });
  })();

  const obtenerBadgeUrgencia = (fechaEntrega) => {
    const urgencia = calcularUrgenciaTesis(fechaEntrega);
    
    const estilos = {
      urgente: 'bg-red-100 text-red-800 border-red-200',
      proximo: 'bg-yellow-100 text-yellow-800 border-yellow-200',
      normal: 'bg-green-100 text-green-800 border-green-200',
      planificado: 'bg-blue-100 text-blue-800 border-blue-200',
      vencida: 'bg-gray-100 text-gray-800 border-gray-200'
    };

    const iconos = {
      urgente: '🚨',
      proximo: '⚠️',
      normal: '✅',
      planificado: '📅',
      vencida: '❌'
    };

    return (
      <span className={`inline-flex items-center px-3 py-1 rounded-full text-xs font-semibold border ${estilos[urgencia.tipo]}`}>
        {iconos[urgencia.tipo]} {urgencia.label}
      </span>
    );
  };

  const obtenerBadgeEstadoTesis = (tesisItem) => {
    const estado = obtenerEstadoTesis(tesisItem);
    
    const badges = {
      disponible: {
        style: 'bg-green-100 text-green-800 border-green-200',
        icon: <CheckSquare size={12} className="mr-1" />,
        text: 'Disponible'
      },
      cotizada: {
        style: 'bg-orange-100 text-orange-800 border-orange-200',
        icon: <DollarSign size={12} className="mr-1" />,
        text: 'Cotizada'
      }
    };

    const badge = badges[estado] || badges.disponible;

    return (
      <span className={`inline-flex items-center px-2 py-1 rounded-full text-xs font-semibold border ${badge.style}`}>
        {badge.icon}
        {badge.text}
      </span>
    );
  };

  // Crear nueva tesis
  const handleCrearTesis = () => {
    setModalNuevaTesisAbierto(true);
  }

  // Cerrar modal tesis
  const handleCerrarTesis = () => {
    setModalNuevaTesisAbierto(false);
  }

  // Abrir modal de cotización
  const handleAbrirCotizacion = (tesisItem) => {
    setTesisSeleccionada(tesisItem);
    setModalCotizacionAbierto(true);
  }

  // Cerrar modal de cotización
  const handleCerrarCotizacion = () => {
    setModalCotizacionAbierto(false);
    setTesisSeleccionada(null);
  }

  // Manejar envío de cotización
  const handleSubmitCotizacion = async (datosForm) => {
    try {
      setCreandoCotizacion(true);
      await onSubmitCotizacion?.(tesisSeleccionada, datosForm);
      handleCerrarCotizacion();
      // Refrescar datos después de crear la cotización
      cargarSolicitudes(true);
    } catch (error) {
      console.error('Error al crear cotización:', error);
    } finally {
      setCreandoCotizacion(false);
    }
  }

  // Obtener información de cotizaciones para tesis
  const obtenerInfoPresupuesto = (cotizaciones) => {
    if (!cotizaciones || cotizaciones.length === 0) {
      return null;
    }

    const cotizacionesValidas = cotizaciones.filter(cot => 
      cot.cotizacion && typeof cot.cotizacion === 'number' && cot.cotizacion > 0
    );

    if (cotizacionesValidas.length === 0) {
      return {
        cantidad: cotizaciones.length,
        estimado: "Pendiente presupuesto",
        rango: null
      };
    }

    const precios = cotizacionesValidas.map(cot => cot.cotizacion);
    const precioMin = Math.min(...precios);
    const precioMax = Math.max(...precios);
    const precioPromedio = precios.reduce((sum, precio) => sum + precio, 0) / precios.length;

    let estimadoTexto;
    let rangoTexto;

    if (cotizacionesValidas.length === 1) {
      estimadoTexto = formatearPrecio(precios[0]);
      rangoTexto = null;
    } else {
      if (precioMin === precioMax) {
        estimadoTexto = formatearPrecio(precioMin);
        rangoTexto = null;
      } else {
        estimadoTexto = formatearPrecio(precioPromedio);
        rangoTexto = `${formatearPrecio(precioMin)} - ${formatearPrecio(precioMax)}`;
      }
    }

    return {
      cantidad: cotizaciones.length,
      cantidadValidas: cotizacionesValidas.length,
      estimado: estimadoTexto,
      rango: rangoTexto,
      promedio: precioPromedio
    }
  }

  // Mostrar error si existe
  if (errorSolicitudes) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-lg p-4">
        <p className="text-red-800">Error al cargar tesis: {errorSolicitudes}</p>
        <button
          onClick={onRefrescar}
          className="mt-2 bg-red-600 text-white px-4 py-2 rounded-lg hover:bg-red-700 transition-colors"
        >
          Reintentar
        </button>
      </div>
    );
  }

  // Cargando tesis
  if (loadingSolicitudes) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="text-center">
          <div className="inline-block h-8 w-8 animate-spin rounded-full border-4 border-solid border-blue-500 border-r-transparent"></div>
          <p className="mt-2 text-gray-600">Cargando proyectos de tesis...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      <NuevaTesis
        isOpen={modalNuevaTesisAbierto}
        onClose={handleCerrarTesis}
        onSubmit={onSubmitNuevaTesis}
        isLoading={creandoTesis}
      />

      <NuevaCotizacion
        isOpen={modalCotizacionAbierto}
        onClose={handleCerrarCotizacion}
        onSubmit={handleSubmitCotizacion}
        isLoading={creandoCotizacion}
        tesisData={tesisSeleccionada}
      />

      {/* Header */}
      <div className="flex flex-col md:flex-row md:items-center md:justify-between gap-4">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <GraduationCap className="text-blue-600" size={28} />
            {isAdmin ? 'Gestión de Proyectos de Tesis' : 'Proyectos de Tesis Disponibles'}
          </h1>
          <p className="text-gray-600">
            {isAdmin 
              ? 'Administración completa de todos los proyectos de tesis del sistema' 
              : 'Proyectos de tesis disponibles para tu área de especialización'
            }
          </p>
        </div>
        
        <div className="flex items-center gap-3">
          <button
            onClick={onRefrescar}
            className="bg-gray-100 text-gray-700 px-4 py-2 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
          >
            <RefreshCw size={16} />
            Actualizar
          </button>
          <button
            onClick={handleCrearTesis}
            className="bg-gradient-to-r from-blue-600 to-blue-700 text-white px-6 py-3 rounded-lg hover:from-blue-700 hover:to-blue-800 transition-all duration-200 shadow-lg hover:shadow-xl flex items-center gap-2 font-semibold"
          >
            <BookmarkPlus size={20} />
            Nuevo Proyecto
          </button>
        </div>
      </div>

      {/* Selector de Estado - Simplificado */}
      {tesis.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 mb-2">Filtrar por Estado de Tesis</h3>
              <div className="flex flex-wrap gap-2">
                <button
                  onClick={() => setFiltros({...filtros, disponibilidad: 'todos'})}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtros.disponibilidad === 'todos' 
                      ? 'bg-gray-900 text-white shadow-md' 
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  Todas ({tesis.length})
                </button>
                <button
                  onClick={() => setFiltros({...filtros, disponibilidad: 'disponibles'})}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtros.disponibilidad === 'disponibles' 
                      ? 'bg-green-600 text-white shadow-md' 
                      : 'bg-green-100 text-green-700 hover:bg-green-200'
                  }`}
                >
                  <CheckSquare size={16} className="inline mr-2" />
                  Disponibles ({tesisDisponibles.length})
                </button>
                <button
                  onClick={() => setFiltros({...filtros, disponibilidad: 'cotizadas'})}
                  className={`px-4 py-2 rounded-lg font-medium transition-all ${
                    filtros.disponibilidad === 'cotizadas' 
                      ? 'bg-orange-600 text-white shadow-md' 
                      : 'bg-orange-100 text-orange-700 hover:bg-orange-200'
                  }`}
                >
                  <DollarSign size={16} className="inline mr-2" />
                  Cotizadas ({tesisCotizadas.length})
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Estadísticas específicas para tesis - Simplificado */}
      {tesis.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-4">
          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Urgentes</p>
                <p className="text-2xl font-bold text-red-600">{stats.urgentes}</p>
              </div>
              <div className="bg-gradient-to-br from-red-500 to-red-600 text-white p-3 rounded-lg">
                <AlertCircle size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Próximas</p>
                <p className="text-2xl font-bold text-yellow-600">{stats.proximas}</p>
              </div>
              <div className="bg-gradient-to-br from-yellow-500 to-orange-500 text-white p-3 rounded-lg">
                <Clock size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Normales</p>
                <p className="text-2xl font-bold text-green-600">{stats.normales}</p>
              </div>
              <div className="bg-gradient-to-br from-green-500 to-green-600 text-white p-3 rounded-lg">
                <CheckCircle size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">Planificadas</p>
                <p className="text-2xl font-bold text-blue-600">{stats.planificadas}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-lg">
                <Calendar size={24} />
              </div>
            </div>
          </div>

          <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100 hover:shadow-md transition-shadow">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-gray-600">{stats.label1}</p>
                <p className="text-2xl font-bold text-blue-600">{stats.total}</p>
              </div>
              <div className="bg-gradient-to-br from-blue-500 to-blue-600 text-white p-3 rounded-lg">
                <BookOpen size={24} />
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Filtros adicionales */}
      {tesis.length > 0 && (
        <div className="bg-white p-6 rounded-xl shadow-sm border border-gray-100">
          <div className="grid grid-cols-1 md:grid-cols-5 gap-4 mb-4">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400" size={20} />
              <input
                type="text"
                placeholder="Buscar por título, estudiante, materia..."
                className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
                value={filtros.busqueda}
                onChange={(e) => setFiltros({...filtros, busqueda: e.target.value})}
              />
            </div>

            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={filtros.nivel}
              onChange={(e) => setFiltros({...filtros, nivel: e.target.value})}
            >
              <option value="todos">Todos los niveles</option>
              <option value="pregrado">Pregrado</option>
              <option value="postgrado">Postgrado</option>
              <option value="maestria">Maestría</option>
              <option value="doctorado">Doctorado</option>
            </select>

            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={filtros.modalidad}
              onChange={(e) => setFiltros({...filtros, modalidad: e.target.value})}
            >
              <option value="todas">Todas las modalidades</option>
              <option value="presencial">Presencial</option>
              <option value="virtual">Virtual</option>
              <option value="mixta">Mixta</option>
            </select>

            <select
              className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:border-transparent transition-all"
              value={filtros.fecha}
              onChange={(e) => setFiltros({...filtros, fecha: e.target.value})}
            >
              <option value="todas">Todas las fechas</option>
              <option value="esta_semana">Esta semana</option>
              <option value="este_mes">Este mes</option>
              <option value="proximo_mes">Próximo mes</option>
            </select>
          </div>

          {/* Selector de vista */}
          <div className="flex items-center gap-2">
            <span className="text-sm font-medium text-gray-700 mr-2">Vista:</span>
            <div className="bg-gray-100 p-1 rounded-lg flex">
              <button
                onClick={() => cambiarVista('cards')}
                className={`px-3 py-2 rounded-md transition-all ${vista === 'cards' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <Grid3X3 size={18} />
              </button>
              <button
                onClick={() => cambiarVista('list')}
                className={`px-3 py-2 rounded-md transition-all ${vista === 'list' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <List size={18} />
              </button>
              <button
                onClick={() => cambiarVista('compact')}
                className={`px-3 py-2 rounded-md transition-all ${vista === 'compact' ? 'bg-white shadow-sm text-blue-600' : 'text-gray-600 hover:text-gray-800'}`}
              >
                <BarChart3 size={18} />
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Contenido según la vista */}
      {tesisFiltradas.length === 0 ? (
        // Estado vacío
        <div className="bg-white rounded-xl shadow-sm border border-gray-100 p-12 text-center">
          <div className="w-20 h-20 bg-blue-100 rounded-full flex items-center justify-center mx-auto mb-6">
            {tesis.length === 0 ? (
              <GraduationCap size={40} className="text-blue-400" />
            ) : (
              <Search size={40} className="text-blue-400" />
            )}
          </div>
          
          <h3 className="text-xl font-semibold text-gray-900 mb-3">
            {tesis.length === 0 
              ? (isAdmin ? 'No hay proyectos de tesis en el sistema' : 'No hay proyectos de tesis disponibles')
              : 'No se encontraron proyectos'
            }
          </h3>
          
          <p className="text-gray-600 mb-6 max-w-md mx-auto">
            {tesis.length === 0 
              ? (isAdmin 
                  ? 'Aún no se han registrado proyectos de tesis. Puedes crear el primer proyecto.' 
                  : 'No hay proyectos de tesis disponibles para tu área en este momento. Te notificaremos cuando haya nuevas oportunidades.'
                )
              : 'Intenta ajustar los filtros de búsqueda para encontrar los proyectos que buscas.'
            }
          </p>
          
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            {tesis.length === 0 ? (
              <>
                <button
                  onClick={onRefrescar}
                  className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors flex items-center gap-2 font-medium"
                >
                  <RefreshCw size={18} />
                  Actualizar
                </button>
                <button
                  onClick={handleCrearTesis}
                  className="bg-blue-600 text-white px-6 py-3 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 font-medium"
                >
                  <BookmarkPlus size={18} />
                  Crear Primer Proyecto
                </button>
              </>
            ) : (
              <button
                onClick={() => setFiltros({ busqueda: '', fecha: 'todas', estado: 'todos', modalidad: 'todas', disponibilidad: 'todos', nivel: 'todos' })}
                className="bg-gray-100 text-gray-700 px-6 py-3 rounded-lg hover:bg-gray-200 transition-colors font-medium"
              >
                Limpiar Filtros
              </button>
            )}
          </div>
        </div>
      ) : (
        // Mostrar tesis según la vista seleccionada
        <>
          {vista === 'cards' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
              {tesisFiltradas.map((tesisItem) => {
                const infoTesis = obtenerInfoTesis(tesisItem);
                const infoPresupuesto = obtenerInfoPresupuesto(tesisItem.cotizaciones);
                const estadoTesis = obtenerEstadoTesis(tesisItem);

                return (
                  <div key={tesisItem.id} className="bg-white rounded-xl shadow-sm border border-gray-100 hover:shadow-lg transition-all duration-200 overflow-hidden">
                    {/* Barra de color superior */}
                    <div className={`h-2 bg-gradient-to-r ${infoTesis.color}`}></div>
                    
                    <div className="p-6">
                      <div className="flex items-start justify-between mb-4">
                        <div className="flex items-center gap-3">
                          <div className={`p-3 rounded-lg bg-gradient-to-br ${infoTesis.color} text-white`}>
                            {infoTesis.icon}
                          </div>
                          <div>
                            <h3 className="font-bold text-gray-900">#{tesisItem.id}</h3>
                            <p className="text-sm text-gray-600">{infoTesis.label}</p>
                          </div>
                        </div>
                        
                        <div className="relative">
                          <button
                            onClick={() => setMenuAbierto(menuAbierto === tesisItem.id ? null : tesisItem.id)}
                            className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                          >
                            <MoreVertical size={16} />
                          </button>
                          
                          {menuAbierto === tesisItem.id && (
                            <div className="absolute right-0 mt-1 w-48 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                              <button
                                onClick={() => {
                                  onVerDetalles?.(tesisItem);
                                  setMenuAbierto(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <Eye size={14} /> Ver detalles
                              </button>
                              {isAdmin && onEditarTesis && (
                                <button
                                  onClick={() => {
                                    onEditarTesis(tesisItem);
                                    setMenuAbierto(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Edit size={14} /> Editar proyecto
                                </button>
                              )}
                              <button
                                onClick={() => {
                                  handleAbrirCotizacion(tesisItem);
                                  setMenuAbierto(null);
                                }}
                                className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                              >
                                <FileText size={14} /> Crear Cotización
                              </button>
                              {onGenerarPDF && (
                                <button
                                  onClick={() => {
                                    onGenerarPDF(tesisItem);
                                    setMenuAbierto(null);
                                  }}
                                  className="w-full text-left px-3 py-2 text-sm hover:bg-gray-50 flex items-center gap-2"
                                >
                                  <Download size={14} /> Generar PDF
                                </button>
                              )}
                            </div>
                          )}
                        </div>
                      </div>

                      <div className="space-y-3 mb-4">
                        <div className="text-lg font-bold text-gray-900 line-clamp-2">
                          {tesisItem.titulo || tesisItem.resumen || 'Proyecto de Tesis'}
                        </div>
                        
                        <div className="text-sm font-medium text-blue-700">
                          📚 {tesisItem.materia}
                        </div>

                        {tesisItem.estudiante && (
                          <div className="text-sm text-gray-600 flex items-center gap-2">
                            <User size={14} />
                            <span>Estudiante: {tesisItem.estudiante}</span>
                          </div>
                        )}

                        <div className="text-sm text-gray-600 line-clamp-3">
                          {tesisItem.resumen || 'Sin descripción disponible'}
                        </div>

                        <div className="flex items-center gap-2 text-sm text-gray-600">
                          <Calendar size={14} />
                          <span>Entrega: {formatearFechaCompleta(tesisItem.fechaentrega)}</span>
                        </div>

                        {tesisItem.director && (
                          <div className="text-sm text-gray-600 flex items-center gap-2">
                            <Users size={14} />
                            <span>Director: {tesisItem.director}</span>
                          </div>
                        )}

                        {tesisItem.cotizaciones && tesisItem.cotizaciones.length > 0 && (
                          <div className="font-medium text-gray-700 bg-gray-50 p-3 rounded-lg">
                            💰 Presupuesto: {infoPresupuesto.estimado}
                            {infoPresupuesto.rango && (
                              <div className="text-xs text-gray-500 mt-1">
                                Rango: {infoPresupuesto.rango}
                              </div>
                            )}
                          </div>
                        )}
                      </div>

                      <div className="mb-4 flex items-center gap-2 flex-wrap">
                        {obtenerBadgeUrgencia(tesisItem.fechaentrega)}
                        {obtenerBadgeEstadoTesis(tesisItem)}
                      </div>

                      <div className="text-xs text-gray-500 mb-3">
                        Asesores interesados: {tesisItem.tutorVistaSolicitud?.length || 0}
                      </div>

                      <div className="flex items-center justify-between">
                        <div className="text-sm text-gray-600">
                          <span className="font-medium">{tesisItem.Estado}</span>
                        </div>
                        <div className="flex items-center gap-2">
                          <button
                            onClick={() => handleAbrirCotizacion(tesisItem)}
                            className="w-8 h-8 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
                            title="Crear cotización"
                          >
                            <FileText size={14} />
                          </button>
                          <button
                            onClick={() => onVerDetalles?.(tesisItem)}
                            className="bg-blue-600 text-white px-4 py-2 rounded-lg hover:bg-blue-700 transition-colors flex items-center gap-2 text-sm font-medium"
                          >
                            <Eye size={14} />
                            Ver Proyecto
                          </button>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {vista === 'list' && (
            <div className="bg-white rounded-xl shadow-sm border border-gray-100 overflow-hidden">
              <div className="overflow-x-auto">
                <table className="w-full">
                  <thead className="bg-blue-50 border-b border-blue-200">
                    <tr>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Proyecto</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Estudiante</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Materia</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Fecha Entrega</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Urgencia</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Estado</th>
                      <th className="text-left py-4 px-6 font-semibold text-gray-700">Acciones</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tesisFiltradas.map((tesisItem, index) => {
                      const infoTesis = obtenerInfoTesis(tesisItem);
                      const estadoTesis = obtenerEstadoTesis(tesisItem);
                      return (
                        <tr key={tesisItem.id} className={`border-b border-gray-100 hover:bg-blue-25 transition-colors ${index % 2 === 0 ? 'bg-white' : 'bg-gray-25'}`}>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-3">
                              <div className={`p-2 rounded-lg bg-gradient-to-br ${infoTesis.color} text-white`}>
                                {React.cloneElement(infoTesis.icon, { size: 16 })}
                              </div>
                              <div>
                                <div className="font-semibold text-gray-900">#{tesisItem.id}</div>
                                <div className="text-sm text-gray-600">{infoTesis.label}</div>
                                <div className="text-sm text-gray-900 font-medium line-clamp-1">
                                  {tesisItem.titulo || tesisItem.resumen}
                                </div>
                              </div>
                            </div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-medium text-gray-900">{tesisItem.estudiante || 'No asignado'}</div>
                            {tesisItem.director && (
                              <div className="text-sm text-gray-600">Dir: {tesisItem.director}</div>
                            )}
                          </td>
                          <td className="py-4 px-6">
                            <div className="font-medium text-gray-900">{tesisItem.materia}</div>
                          </td>
                          <td className="py-4 px-6">
                            <div className="text-gray-900">{formatearFechaCompleta(tesisItem.fechaentrega)}</div>
                          </td>
                          <td className="py-4 px-6">
                            {obtenerBadgeUrgencia(tesisItem.fechaentrega)}
                          </td>
                          <td className="py-4 px-6">
                            {obtenerBadgeEstadoTesis(tesisItem)}
                          </td>
                          <td className="py-4 px-6">
                            <div className="flex items-center gap-2">
                              <button
                                onClick={() => handleAbrirCotizacion(tesisItem)}
                                className="w-8 h-8 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
                                title="Crear cotización"
                              >
                                <FileText size={12} />
                              </button>
                              <button
                                onClick={() => onVerDetalles?.(tesisItem)}
                                className="bg-blue-600 text-white px-3 py-1.5 rounded-md hover:bg-blue-700 transition-colors text-sm flex items-center gap-1"
                              >
                                <Eye size={12} />
                                Ver
                              </button>
                              {isAdmin && onEditarTesis && (
                                <button
                                  onClick={() => onEditarTesis(tesisItem)}
                                  className="bg-gray-100 text-gray-700 px-3 py-1.5 rounded-md hover:bg-gray-200 transition-colors text-sm flex items-center gap-1"
                                >
                                  <Edit size={12} />
                                  Editar
                                </button>
                              )}
                            </div>
                          </td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </div>
          )}

          {vista === 'compact' && (
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
              {tesisFiltradas.map((tesisItem) => {
                const infoTesis = obtenerInfoTesis(tesisItem);
                const urgencia = calcularUrgenciaTesis(tesisItem.fechaentrega);
                const estado = obtenerEstadoTesis(tesisItem);
                
                return (
                  <div key={tesisItem.id} className="bg-white rounded-lg shadow-sm border border-gray-100 hover:shadow-md transition-all duration-200 p-4">
                    <div className="flex items-center justify-between mb-3">
                      <div className="flex items-center gap-2">
                        <div className={`p-1.5 rounded-md bg-gradient-to-br ${infoTesis.color} text-white`}>
                          {React.cloneElement(infoTesis.icon, { size: 14 })}
                        </div>
                        <div>
                          <div className="font-semibold text-sm text-gray-900">#{tesisItem.id}</div>
                          <div className="text-xs text-gray-600">{infoTesis.label}</div>
                        </div>
                      </div>
                      <div className="relative">
                        <button
                          onClick={() => setMenuAbierto(menuAbierto === tesisItem.id ? null : tesisItem.id)}
                          className="p-1 hover:bg-gray-100 rounded-md transition-colors"
                        >
                          <MoreVertical size={14} />
                        </button>
                        
                        {menuAbierto === tesisItem.id && (
                          <div className="absolute right-0 mt-1 w-32 bg-white border border-gray-200 rounded-lg shadow-lg z-10">
                            <button
                              onClick={() => {
                                onVerDetalles?.(tesisItem);
                                setMenuAbierto(null);
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-1"
                            >
                              <Eye size={12} /> Ver
                            </button>
                            {isAdmin && onEditarTesis && (
                              <button
                                onClick={() => {
                                  onEditarTesis(tesisItem);
                                  setMenuAbierto(null);
                                }}
                                className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-1"
                              >
                                <Edit size={12} /> Editar
                              </button>
                            )}
                            <button
                              onClick={() => {
                                handleAbrirCotizacion(tesisItem);
                                setMenuAbierto(null);
                              }}
                              className="w-full text-left px-2 py-1.5 text-xs hover:bg-gray-50 flex items-center gap-1"
                            >
                              <FileText size={12} /> Cotizar
                            </button>
                          </div>
                        )}
                      </div>
                    </div>

                    <div className="space-y-2 mb-3">
                      <div className="text-xs text-gray-900 font-bold truncate">
                        {tesisItem.titulo || tesisItem.resumen}
                      </div>
                      <div className="text-xs text-gray-600">{tesisItem.materia}</div>
                      {tesisItem.estudiante && (
                        <div className="text-xs text-gray-600">👤 {tesisItem.estudiante}</div>
                      )}
                      <div className="flex justify-between items-center gap-1">
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          urgencia.tipo === 'urgente' ? 'bg-red-100 text-red-700' :
                          urgencia.tipo === 'proximo' ? 'bg-yellow-100 text-yellow-700' :
                          urgencia.tipo === 'vencida' ? 'bg-gray-100 text-gray-700' :
                          urgencia.tipo === 'normal' ? 'bg-green-100 text-green-700' :
                          'bg-blue-100 text-blue-700'
                        }`}>
                          {urgencia.label}
                        </span>
                        <span className={`text-xs px-2 py-1 rounded-full font-medium ${
                          estado === 'disponible' ? 'bg-green-100 text-green-700' : 
                          'bg-orange-100 text-orange-700'
                        }`}>
                          {estado === 'disponible' ? '✅' : '💰'}
                        </span>
                      </div>
                    </div>

                    <div className="text-xs text-gray-500 mb-3 truncate">
                      📅 {formatearFechaCompleta(tesisItem.fechaentrega)}
                    </div>

                    <div className="flex items-center gap-2">
                      <button
                        onClick={() => handleAbrirCotizacion(tesisItem)}
                        className="w-8 h-8 bg-blue-600 text-white rounded-full hover:bg-blue-700 transition-colors flex items-center justify-center"
                        title="Crear cotización"
                      >
                        <FileText size={12} />
                      </button>
                      <button
                        onClick={() => onVerDetalles?.(tesisItem)}
                        className="flex-1 bg-blue-600 text-white py-1.5 rounded-md hover:bg-blue-700 transition-colors text-xs font-medium"
                      >
                        Ver Proyecto
                      </button>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </>
      )}

      {/* Click fuera para cerrar menús */}
      {menuAbierto && (
        <div
          className="fixed inset-0 z-0"
          onClick={() => setMenuAbierto(null)}
        />
      )}
    </div>
  );
};

export default DashboardTesis;